import { ChartContainer, type ChartConfig } from "@/components/ui/chart"

/**
 * The only way a chart gets drawn on this screen.
 *
 * It exists so that the accessible reading of a series is STRUCTURAL rather than
 * a convention somebody has to remember. `rows` is required, so a chart cannot
 * ship without the full series in a `<table>` behind it — an `sr-only` table is
 * the one treatment that gives a screen-reader user the same data a sighted user
 * gets from the shape, rather than a summary somebody wrote by hand and let go
 * stale. The plot itself is `role="img"` with a label, which collapses a few
 * hundred `<path>` elements into one announcement instead of into noise.
 *
 * **`aspect-auto` is load-bearing.** `components/ui/chart` bakes `aspect-video`
 * into its default class list, and a 16:9 box on a 375px phone is 211px of
 * chart under a 44px control — worse, it fights any height you set. Tailwind's
 * merge only drops a class an incoming one supersedes, and a `h-*` does not
 * supersede an `aspect-*`, so the override has to name the aspect property. It
 * is written once, here, rather than at every call site: the point of a frame is
 * that the next chart cannot forget.
 */

export type ChartFrameColumn = {
  key: string
  label: string
}

export type ChartFrameRow = {
  key: string
  /** Positional, matching `columns`. The first cell is the row header. */
  cells: string[]
}

export function ChartFrame({
  label,
  note,
  config,
  columns,
  rows,
  testId,
  children,
}: {
  /** Announced as the chart's name and used as the table caption. */
  label: string
  /** Provenance or a bucketing note. Sits under the plot, in the reading order. */
  note?: string
  config: ChartConfig
  columns: ChartFrameColumn[]
  rows: ChartFrameRow[]
  testId?: string
  children: React.ComponentProps<typeof ChartContainer>["children"]
}) {
  return (
    <div className="space-y-2" data-testid={testId}>
      <ChartContainer
        config={config}
        role="img"
        aria-label={label}
        className="aspect-auto h-[180px] w-full md:h-[220px]"
      >
        {children}
      </ChartContainer>

      <table className="sr-only" data-testid={testId ? `${testId}-table` : undefined}>
        <caption>{label}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} scope="col">
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              {row.cells.map((cell, index) =>
                index === 0 ? (
                  <th key={columns[index]?.key ?? index} scope="row">
                    {cell}
                  </th>
                ) : (
                  <td key={columns[index]?.key ?? index}>{cell}</td>
                )
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {note ? <p className="text-xs text-muted-foreground">{note}</p> : null}
    </div>
  )
}
